import React, { ForwardRefRenderFunction, forwardRef } from "react";
//#region redux
import { ThunkDispatch } from "redux-thunk";
import { useDispatch } from "react-redux";
import { setProfileName } from "store/actions/authActions";
import { setProfileLanguage } from "store/actions/commonActions";
//#endregion
//#region types
import { ReceiptFormTypes, SettingsFormTypes } from "models/formTypes";
import { FormHandles, TableListFieldProps } from "models/propTypes/genericFormProps";
import { FormikProps } from "formik";
import { FormFieldConstants, IconDefinitionConstants, SettingsConstants } from "utils/constants";
import { SessionHelper } from "utils/helpers";
import { Person } from "models/stateTypes/authState";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { UploadedFile } from "models/utilTypes/fileUtilsTypes";
import { ProfileSettingsFormContainerProps } from "models/propTypes/settingsPropTypes";
import { CurrencyEnums, StyleEnums } from "models/enums";
import { TranslationKeys } from "translations";
//#endregion
//#region components
import ProfileSettingsForm from "./profileSettingsForm";
import { Skeletons, useMountedState } from "components/common";
import { AddPaymentMethodFormContainer } from "components/receipt";
import { Chip, Grid } from "@mui/material";
import { useReceiptPaymentMethodService } from "serviceHooks";
//#endregion
import { LanguageHelper, LocalizationHelperInstance } from "utils/helpers";
import { SettingsService } from "services";
import * as Yup from "yup";
import cssExports from "theme/styles/main.module.scss";

const ProfileSettingsFormContainer: ForwardRefRenderFunction<FormHandles, ProfileSettingsFormContainerProps> = (
  props,
  ref
) => {
  const receiptPaymentMethodService = useReceiptPaymentMethodService();
  const [loading, setLoading] = React.useState<boolean>(true);
  const dispatch: ThunkDispatch<any, any, any> = useDispatch();
  const { getMounted } = useMountedState();

  const getPaymentMethodsValidationScheme = () => {
    return Yup.array().of(
      Yup.object<ReceiptFormTypes.AddPaymentMethod>({
        id: Yup.number(),
        name: Yup.string(),
        amount: Yup.number(),
        isActive: Yup.boolean(),
        currencyMarkup: Yup.number(),
        paymentMethodType: Yup.string(),
      })
    );
  };

  const getCompanySpecificEmailsValidationScheme = () => {
    return Yup.array().of(
      Yup.object<WintSuperkollAPIModel.PersonCompanyEmail>({
        companyId: Yup.number(),
        companyName: Yup.string(),
        email: Yup.string(),
        personId: Yup.number(),
      })
    );
  };

  const validationSchema = Yup.object<SettingsFormTypes.ProfileSettings>({
    name: Yup.string(),
    phone: Yup.string().validatePhone(TranslationKeys.Validations.InvalidPhone),
    includeOverdueInvoices: Yup.boolean(),
    noDailyMailOnWeekends: Yup.boolean(),
    useCompanySpecificEmail: Yup.boolean(),
    companySpecificEmails: getCompanySpecificEmailsValidationScheme(),
    ownExpensePaymentMethods: getPaymentMethodsValidationScheme(),
    bankCardPaymentMethods: getPaymentMethodsValidationScheme(),
    creditCardPaymentMethods: getPaymentMethodsValidationScheme(),
    settlementPaymentMethods: getPaymentMethodsValidationScheme(),
    companyCashPaymentMethods: getPaymentMethodsValidationScheme(),
    wintCardPaymentMethods: getPaymentMethodsValidationScheme(),
    tableSizeId: Yup.number(),
    profileImage: Yup.array()
      .max(1, TranslationKeys.Settings.OneProfileImage)
      .of(
        Yup.object<UploadedFile>({
          data: Yup.string(),
          contentType: Yup.string(),
          fileName: Yup.string(),
          fileSize: Yup.number(),
        })
      ),
    languageKey: Yup.string().required(TranslationKeys.Validations.Required),
    languageName: Yup.string().required(TranslationKeys.Validations.Required),
    bankAccount: Yup.string(),
    bankAccountClearing: Yup.string(),
  });

  const getPaymentMethods = (
    paymentMethods: WintSuperkollAPIModel.ReceiptPaymentMethodListItem[] | undefined,
    type: WintSuperkollAPIModel.ReceiptPaymentMethodType
  ) => {
    return paymentMethods
      ?.filter((m) => m.type === type)
      .map(
        (method): ReceiptFormTypes.AddPaymentMethod => ({
          id: method.id,
          name: method.name ?? "",
          amount: method.amount,
          isActive: method.isActive,
          currencyMarkup: method.currencyMarkup,
          canDeactivate: method.canDeactivate,
          type: method.type,
        })
      );
  };

  const [defaultValues, setDefaultValues] = React.useState<SettingsFormTypes.ProfileSettings>({
    languageKey: SettingsConstants.DefaultInvoiceLanguageCode,
    languageName: LanguageHelper.getLanguageText(SettingsConstants.DefaultInvoiceLanguageCode),
  });

  const handleSubmit = (values: SettingsFormTypes.ProfileSettings) => {
    const profImage = values.profileImage && values.profileImage[0];
    const settings = {
      name: values.name,
      phone: values.phone,
      profileImage:
        profImage &&
        ({ imageData: profImage.data, contentType: profImage.contentType } as WintSuperkollAPIModel.ProfileImage),
      includeOverdueInvoices: values.includeOverdueInvoices,
      noDailyMailOnWeekends: values.noDailyMailOnWeekends,
      useCompanySpecificEmail: values.useCompanySpecificEmail,
      companySpecificEmails: values.companySpecificEmails,
      tableSize: values.tableSizeId,
      selectedLanguage: values.languageKey,
      bankAccount: values.bankAccount,
      bankAccountClearing: values.bankAccountClearing,
      additionalBankAccount: values.additionalBankAccount,
      additionalBankAccountClearing: values.additionalBankAccountClearing
    } as WintSuperkollAPIModel.ProfileSettingsPut;
    return SettingsService.updateProfileSettings(settings).then((res) => {
      if (values.name && values.name !== defaultValues?.name) {
        dispatch(setProfileName(values.name));
        const userSession = SessionHelper.getSessionItem<Person>("user_session");
        SessionHelper.saveToSession("user_session", { ...userSession, name: values.name });
      }
      dispatch(setProfileLanguage(values.languageKey!));
      if (getMounted()) {
        setDefaultValues(values);
      }
    });
  };

  React.useEffect(() => {
    SettingsService.getProfileSettings().then((profileSettings) => {
      if (getMounted()) {
        setDefaultValues({
          name: profileSettings?.name,
          mail: profileSettings?.mail,
          ownExpensePaymentMethods: getPaymentMethods(
            profileSettings.paymentMethods,
            WintSuperkollAPIModel.ReceiptPaymentMethodType.OwnExpense
          ),
          bankCardPaymentMethods: getPaymentMethods(
            profileSettings.paymentMethods,
            WintSuperkollAPIModel.ReceiptPaymentMethodType.BankCard
          ),
          creditCardPaymentMethods: getPaymentMethods(
            profileSettings.paymentMethods,
            WintSuperkollAPIModel.ReceiptPaymentMethodType.CreditCard
          ),
          companyCashPaymentMethods: getPaymentMethods(
            profileSettings.paymentMethods,
            WintSuperkollAPIModel.ReceiptPaymentMethodType.Cash
          ),
          settlementPaymentMethods: getPaymentMethods(
            profileSettings.paymentMethods,
            WintSuperkollAPIModel.ReceiptPaymentMethodType.Settlement
          ),
          wintCardPaymentMethods: getPaymentMethods(
            profileSettings.paymentMethods,
            WintSuperkollAPIModel.ReceiptPaymentMethodType.WintCard
          ),
          phone: profileSettings?.phone,
          includeOverdueInvoices: profileSettings?.includeOverdueInvoices,
          noDailyMailOnWeekends: profileSettings?.noDailyMailOnWeekends,
          useCompanySpecificEmail: profileSettings?.useCompanySpecificEmail,
          companySpecificEmails: profileSettings?.companySpecificEmails,
          profileImage: profileSettings?.profileImage
            ? [
                {
                  data: profileSettings?.profileImage?.imageData,
                  contentType: profileSettings?.profileImage?.contentType,
                } as UploadedFile,
              ]
            : [],
          languageKey: profileSettings?.selectedLanguage ?? SettingsConstants.DefaultInvoiceLanguageCode,
          languageName: LanguageHelper.getLanguageText(
            profileSettings?.selectedLanguage ?? SettingsConstants.DefaultInvoiceLanguageCode
          ),
          tableSizeId: profileSettings?.tableSize,
          tableSizeName: LocalizationHelperInstance.getFormattedMessage(
            `Settings.TableSizeType_${profileSettings?.tableSize}`
          ),
          bankAccount: profileSettings?.bankAccount,
          bankAccountClearing: profileSettings?.bankAccountClearing,
          additionalBankAccount: profileSettings?.additionalBankAccount,
          additionalBankAccountClearing: profileSettings?.additionalBankAccountClearing
        });
        setLoading(false);
      }
    });
  }, []);

  const onPaymentMethodActivateOrDeactivate = async (
    rowIndex: number,
    row: ReceiptFormTypes.AddPaymentMethod,
    formikProps: FormikProps<SettingsFormTypes.ProfileSettings>
  ) => {
    const { values, setFieldValue } = formikProps;
    const updatedPaymentMethods = values.ownExpensePaymentMethods ? [...values.ownExpensePaymentMethods] : [];
    updatedPaymentMethods.forEach((x) => {
      if (x.id === row.id) x.isActive = !x.isActive;
    });

    row.id &&
      receiptPaymentMethodService
        .update(row.id, {
          name: row.name,
          currencyMarkup: row.currencyMarkup,
          isActive: row.isActive,
        } as WintSuperkollAPIModel.ReceiptPaymentMethodPut)
        .then((res) => {
          setFieldValue(FormFieldConstants.Settings.ProfileSettings.OwnExpensePaymentMethods, updatedPaymentMethods);
        });
  };

  const getPaymentMethodsTableExtraProps = (hideAddNewButton: boolean) => {
    return {
      resultTableProperties: {
        name: {
          formatter: (value: ReceiptFormTypes.AddPaymentMethod) => (
            <Grid item className={cssExports.tableListMainText}>
              {value.name}
            </Grid>
          ),
        },
        amount: {
          formatter: (value: ReceiptFormTypes.AddPaymentMethod) => (
            <Grid item container direction="column">
              {
                <Grid item className={cssExports.tableListMainText}>
                  {value.amount !== undefined &&
                    LocalizationHelperInstance.getFormattedCurrency(value.amount, CurrencyEnums.CurrencyCode.SEK)}
                </Grid>
              }
              {value.currencyMarkup && (
                <Grid item>{`${LocalizationHelperInstance.getFormattedMessage(
                  TranslationKeys.Settings.CurrencySurcharge
                )}: ${value.currencyMarkup} %`}</Grid>
              )}
            </Grid>
          ),
          width: "25%",
          noWrap: true,
        },
        isActive: {
          formatter: (value: ReceiptFormTypes.AddPaymentMethod) => (
            <Chip
              label={LocalizationHelperInstance.getFormattedMessage(
                value.isActive ? TranslationKeys.Common.Active : TranslationKeys.Common.InActive
              )}
              className={value.isActive ? cssExports.greenTag : cssExports.redTag}
            />
          ),
          alignment: "center",
          width: "100px",
        },
      },
      addItemForm: (values?: SettingsFormTypes.ProfileSettings) => (
        <AddPaymentMethodFormContainer
          submitResultFormatter={(
            result: WintSuperkollAPIModel.ReceiptPaymentMethod
          ): ReceiptFormTypes.AddPaymentMethod => {
            const method = values?.ownExpensePaymentMethods?.find((m) => m.id === result.id);
            return {
              id: result.id,
              name: result.name ?? "",
              amount: method?.amount ?? 0,
              currencyMarkup: result.currencyMarkup,
              isActive: result.isActive,
              canDeactivate: method ? method.canDeactivate : true,
              type: result.type,
            };
          }}
        ></AddPaymentMethodFormContainer>
      ),
      editDialogDisableSave: (row: ReceiptFormTypes.AddPaymentMethod) =>
        !receiptPaymentMethodService.canEdit(row.type, row.canDeactivate),
      orderByProperty: FormFieldConstants.Receipt.AddPaymentMethod.Id,
      entityText: TranslationKeys.Settings.PaymentMethod,
      moreActions: (row: ReceiptFormTypes.AddPaymentMethod) =>
        row.canDeactivate && row.type === WintSuperkollAPIModel.ReceiptPaymentMethodType.OwnExpense
          ? [
              {
                description: LocalizationHelperInstance.getFormattedMessage(
                  row.isActive ? TranslationKeys.Common.Inactivate : TranslationKeys.Common.Activate
                ),
                icon: IconDefinitionConstants.WStop,
                highlightType: row.isActive ? StyleEnums.DisplayType.Danger : undefined,
                onClickHandler: onPaymentMethodActivateOrDeactivate,
              },
            ]
          : [],
      activeKey: FormFieldConstants.Receipt.AddPaymentMethod.IsActive,
      hideAddNewButton: hideAddNewButton,
      hideMoreActionsInForm: true,
    } as TableListFieldProps<ReceiptFormTypes.AddPaymentMethod>;
  };

  return loading ? (
    <Skeletons.FormSkeleton />
  ) : (
    <ProfileSettingsForm
      ref={ref}
      {...props}
      validationSchema={validationSchema}
      handleSubmit={handleSubmit}
      defaultValues={defaultValues}
      getPaymentMethodsTableExtraProps={getPaymentMethodsTableExtraProps}
    />
  );
};

export default forwardRef(ProfileSettingsFormContainer);
